import { useEffect, useState } from "react";
import type { ReactElement, SVGProps } from "react";
import {
  ArrowRight,
  BookOpen,
  Check,
  CircleGauge,
  MapPinned,
  MessageCircle,
  Plane,
  PhoneCall,
  QrCode,
  RadioTower,
  RefreshCw,
  Settings,
  ShieldCheck,
  Signal,
  Smartphone,
  Wifi,
  Wrench,
  X,
} from "lucide-react";
import { Footer } from "@/components/footer";
import { Navbar } from "@/components/navbar";
import { FAQItem } from "@/components/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  MONRA_ESIM_ANDROID_DOWNLOAD_URL,
  MONRA_ESIM_DOWNLOAD_URL,
  MONRA_ESIM_IOS_DOWNLOAD_URL,
} from "@/lib/app-links";
import {
  getVisitorDevice,
  type VisitorDevice,
} from "@/lib/visitor-device";

const mainNavLinks = [
  { href: "#destinations", label: "Destinations" },
  { href: "#how-it-works", label: "How it works" },
  { href: "#why-monra", label: "Why Monra" },
  { href: "#support", label: "Support" },
];

const journeySteps = [
  {
    number: "01",
    title: "Choose where you are going",
    description:
      "Browse eSIM data options for your destination before your trip begins.",
  },
  {
    number: "02",
    title: "Install before departure",
    description:
      "Set up your digital SIM ahead of time, without swapping a physical card.",
  },
  {
    number: "03",
    title: "Connect when you land",
    description:
      "Turn on your eSIM at your destination and join a supported local network.",
  },
];

const readinessNotes = [
  {
    title: "Compatible device",
    description: "Your phone must support eSIM and be carrier-unlocked.",
    label: "Device check",
  },
  {
    title: "Data-only service",
    description:
      "Keep your usual SIM for calls while Monra supplies travel data.",
    label: "Service type",
  },
  {
    title: "Activate on arrival",
    description:
      "Install in advance, then turn on your travel eSIM at your destination.",
    label: "Travel timing",
  },
];

const destinations = [
  { code: "JP", name: "Japan", region: "Asia", fromPrice: "$4.50" },
  { code: "FR", name: "France", region: "Europe", fromPrice: "$3.99" },
  { code: "ES", name: "Spain", region: "Europe", fromPrice: "$0.82" },
  { code: "GB", name: "United Kingdom", region: "Europe", fromPrice: "$0.82" },
  { code: "ID", name: "Indonesia", region: "Asia", fromPrice: "$3.50" },
  { code: "TH", name: "Thailand", region: "Asia", fromPrice: "$3.75" },
  { code: "IT", name: "Italy", region: "Europe", fromPrice: "$3.99" },
  { code: "AU", name: "Australia", region: "Oceania", fromPrice: "$4.95" },
  { code: "SG", name: "Singapore", region: "Asia", fromPrice: "$3.90" },
  { code: "MX", name: "Mexico", region: "North America", fromPrice: "$4.50" },
  { code: "KR", name: "South Korea", region: "Asia", fromPrice: "$4.25" },
  { code: "MA", name: "Morocco", region: "Africa", fromPrice: "$4.50" },
];

const MONRA_ESIM_API_BASE_URL =
  import.meta.env.VITE_MONRA_ESIM_API_BASE_URL?.replace(/\/$/, "") ??
  "https://us-central1-monra-esim-prod.cloudfunctions.net/api";

const PUBLIC_BUNDLES_ENDPOINT = `${MONRA_ESIM_API_BASE_URL}/public/bundles`;

const proofPoints = [
  {
    title: "Destination-first",
    detail: "Start with the country you are visiting.",
    icon: Wifi,
  },
  {
    title: "Digital setup",
    detail: "No store visit or SIM tray shuffle.",
    icon: Smartphone,
  },
  {
    title: "Clear checks",
    detail: "Device and plan details stay visible.",
    icon: ShieldCheck,
  },
  {
    title: "Travel data",
    detail: "Built for maps, rides, messages, and browsing.",
    icon: MapPinned,
  },
];

const comparisonOptions = [
  {
    name: "Airport SIM kiosk",
    summary:
      "Works when available, but the tradeoffs usually show up right after landing.",
    details: [
      { text: "Buy after landing", tone: "con" },
      { text: "Requires SIM tray access", tone: "con" },
      {
        text: "Can mean queues, ID checks, and unclear tourist-plan terms",
        tone: "con",
      },
    ],
  },
  {
    name: "Carrier roaming",
    summary:
      "Convenient when enabled, but it can become expensive or limited without much warning.",
    details: [
      { text: "Daily fees can add up quickly", tone: "con" },
      { text: "Speed caps and fair-use limits may apply", tone: "con" },
      { text: "Roaming must be enabled before travel", tone: "con" },
    ],
  },
  {
    name: "Monra eSIM",
    summary:
      "Plan ahead, install digitally, and connect when you reach a supported destination.",
    details: [
      { text: "Choose before departure", tone: "pro" },
      { text: "No physical SIM swap", tone: "pro" },
      { text: "Plan details shown before checkout", tone: "pro" },
    ],
    featured: true,
  },
];

const travelUseCases = [
  {
    title: "Find your way out of the airport",
    body: "Use maps, rides, and hotel directions as soon as local coverage is available.",
    icon: MapPinned,
  },
  {
    title: "Keep messages moving",
    body: "Stay reachable on chat apps while your usual SIM remains available for calls.",
    icon: Wifi,
  },
  {
    title: "Handle bookings on the move",
    body: "Open confirmations, check-in pages, and travel updates without hunting for public Wi-Fi.",
    icon: Smartphone,
  },
  {
    title: "Work between stops",
    body: "Use travel data for email, documents, and light browsing when you are away from the hotel.",
    icon: Plane,
  },
  {
    title: "Keep a backup route online",
    body: "Prepare a travel data option before departure so connectivity is not an arrival-day problem.",
    icon: ShieldCheck,
  },
];

type SupportTabId = "troubleshooting" | "issues" | "faqs";

const supportQuickLinks: {
  id: SupportTabId;
  title: string;
  description: string;
  icon: typeof Wrench;
}[] = [
  {
    id: "faqs",
    title: "FAQs",
    description: "Billing, compatibility, usage, and installation details.",
    icon: BookOpen,
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    description: "Guided setup checks for common connection issues.",
    icon: Wrench,
  },
  {
    id: "issues",
    title: "Common issues",
    description: "Fast answers for QR, data, calls, and speed problems.",
    icon: MessageCircle,
  },
];

const troubleshootingSteps = [
  {
    title: "Enable data roaming",
    description:
      "Open Cellular or Mobile settings, choose your eSIM line, and turn on Data Roaming.",
    icon: Settings,
  },
  {
    title: "Restart your device",
    description:
      "Turn your phone off and on again. A restart often clears temporary network issues.",
    icon: RefreshCw,
  },
  {
    title: "Check network selection",
    description:
      "Switch between automatic and manual network selection, then try a supported local operator.",
    icon: RadioTower,
  },
  {
    title: "Verify the eSIM is active",
    description:
      "Confirm the plan is active, installed on this device, and still has data available.",
    icon: ShieldCheck,
  },
  {
    title: "Check local coverage",
    description:
      "Move to an open area and compare signal with other nearby devices when possible.",
    icon: Signal,
  },
  {
    title: "Reset network settings",
    description:
      "Use this as a last step. It can remove saved Wi-Fi passwords and paired network settings.",
    icon: CircleGauge,
  },
];

const troubleshootingIssues = [
  {
    title: "eSIM is not connecting to a network",
    description:
      "Enable data roaming, confirm your phone is unlocked, restart the device, and try manual network selection in a coverage area.",
    icon: Signal,
  },
  {
    title: "QR code is not scanning",
    description:
      "Use a stable Wi-Fi connection, improve lighting, clean the camera lens, and zoom in on the QR code if needed.",
    icon: QrCode,
  },
  {
    title: "Data is not working after installation",
    description:
      "Wait a few minutes, enable data roaming, and make sure the eSIM line is selected for cellular data.",
    icon: Wifi,
  },
  {
    title: "Calls are not working on the eSIM",
    description:
      "Most travel eSIM plans are data-only. Use internet calling apps when voice service is not included in your plan.",
    icon: PhoneCall,
  },
  {
    title: "Internet speed feels slow",
    description:
      "Speed can vary by location, signal, and network congestion. Try moving nearby or selecting another available operator manually.",
    icon: CircleGauge,
  },
];

const esimFaqs = [
  {
    question: "When should I install my eSIM?",
    answer:
      "Install it before your trip while you have a stable internet connection. Activate mobile data when you arrive at your destination and are ready to use the plan.",
  },
  {
    question: "Can I keep my normal number?",
    answer:
      "Yes. Monra eSIM is for travel data. Keep your regular SIM active for calls, SMS, and your usual number if your device supports using both lines.",
  },
  {
    question: "Does this include calls or SMS?",
    answer:
      "Monra eSIM plans are designed for mobile data. Voice calls and SMS are not included unless a specific plan says otherwise before checkout.",
  },
  {
    question: "Can I get a refund?",
    answer:
      "Monra eSIM purchases are generally final once the eSIM or activation details have been delivered, installed, activated, or used. If something goes wrong because of a Monra or provider-side issue, contact support before deleting the eSIM so we can review the case, help resolve it, or issue a refund where appropriate.",
  },
  {
    question: "Can I top up my eSIM?",
    answer:
      "Yes. If top-ups are available for your active plan, open the plan details and choose the top-up option to purchase additional data.",
  },
  {
    question: "Is my device compatible with eSIM?",
    answer:
      "Many recent phones support eSIM, including iPhone XS or newer, Samsung Galaxy S20 or newer, and Google Pixel 3 or newer. Compatibility can vary by model, region, and carrier, so make sure your device is carrier-unlocked and confirm eSIM support in your device settings or manufacturer specifications before buying.",
  },
  {
    question: "Can I delete and reinstall an eSIM?",
    answer:
      "Yes, you can delete and reinstall an eSIM on the same device. Each eSIM can only be added to one device, so you cannot move it to another device after the first installation.",
  },
  {
    question: "When does my eSIM data plan start?",
    answer:
      "Your data plan starts when your eSIM first connects to a supported network in your destination. The validity period begins from that first connection, so enable the eSIM when you are ready to use travel data.",
  },
];

type Destination = (typeof destinations)[number];
type MobileDevice = Exclude<VisitorDevice, "laptop">;
type LandingBundleStatus = "loading" | "ready" | "fallback";

type PublicBundlePlan = {
  data: string;
  price: number;
  speed: string;
  validityDays?: number;
};

type PublicDestinationBundles = {
  countryCode: string;
  startingPrice: number | null;
  plans: PublicBundlePlan[];
};

type PublicBundlesResponse = {
  countries: PublicDestinationBundles[];
};

type DestinationPlanSummary = {
  status: LandingBundleStatus;
  fromPrice: string;
  plans: PublicBundlePlan[];
  planCountLabel: string;
};

function isPublicBundlePlan(value: unknown): value is PublicBundlePlan {
  if (!value || typeof value !== "object") return false;

  const plan = value as {
    data?: unknown;
    price?: unknown;
    speed?: unknown;
    validityDays?: unknown;
  };

  return (
    typeof plan.data === "string" &&
    typeof plan.price === "number" &&
    typeof plan.speed === "string" &&
    (typeof plan.validityDays === "number" ||
      typeof plan.validityDays === "undefined")
  );
}

function isPublicBundlesResponse(
  value: unknown,
): value is PublicBundlesResponse {
  if (!value || typeof value !== "object") return false;

  const countries = (value as { countries?: unknown }).countries;
  if (!Array.isArray(countries)) return false;

  return countries.every((country) => {
    if (!country || typeof country !== "object") return false;

    const destination = country as {
      countryCode?: unknown;
      startingPrice?: unknown;
      plans?: unknown;
    };

    return (
      typeof destination.countryCode === "string" &&
      (typeof destination.startingPrice === "number" ||
        destination.startingPrice === null) &&
      Array.isArray(destination.plans) &&
      destination.plans.every(isPublicBundlePlan)
    );
  });
}

function formatPlanPrice(price: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
  }).format(price);
}

function formatPlanLabel(plan: PublicBundlePlan): string {
  return plan.validityDays
    ? `${plan.data} / ${plan.validityDays} days`
    : plan.data;
}

function useLandingBundles() {
  const [bundlesByCountry, setBundlesByCountry] = useState<
    Record<string, PublicDestinationBundles>
  >({});
  const [status, setStatus] = useState<LandingBundleStatus>("loading");

  useEffect(() => {
    const controller = new AbortController();

    async function loadLandingBundles() {
      try {
        const response = await fetch(PUBLIC_BUNDLES_ENDPOINT, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load bundles: ${response.status}`);
        }

        const data: unknown = await response.json();
        if (!isPublicBundlesResponse(data)) {
          throw new Error("Unexpected public bundles response");
        }

        setBundlesByCountry(
          Object.fromEntries(
            data.countries.map((country) => [
              country.countryCode.toUpperCase(),
              {
                ...country,
                countryCode: country.countryCode.toUpperCase(),
                plans: [...country.plans].sort((a, b) => a.price - b.price),
              },
            ]),
          ),
        );
        setStatus("ready");
      } catch (error) {
        if (controller.signal.aborted) return;

        console.warn("Using fallback destination prices.", error);
        setStatus("fallback");
      }
    }

    void loadLandingBundles();

    return () => controller.abort();
  }, []);

  return { bundlesByCountry, status };
}

function getDestinationPlanSummary(
  destination: Destination,
  bundlesByCountry: Record<string, PublicDestinationBundles>,
  status: LandingBundleStatus,
): DestinationPlanSummary {
  const countryBundles = bundlesByCountry[destination.code];
  const plans = countryBundles?.plans ?? [];
  const startingPrice = countryBundles?.startingPrice;
  const hasLivePrice = typeof startingPrice === "number";

  return {
    status,
    fromPrice: hasLivePrice
      ? formatPlanPrice(startingPrice)
      : destination.fromPrice,
    plans,
    planCountLabel:
      plans.length > 0
        ? `${plans.length} plan${plans.length === 1 ? "" : "s"}`
        : status === "loading"
          ? "Loading plans"
          : "Plans in app",
  };
}

function AppleLogoIcon(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M16.36 1.43c0 1.02-.42 1.96-1.14 2.69-.77.79-1.82 1.38-2.84 1.3-.13-.98.38-2.04 1.06-2.77.75-.8 2.05-1.41 2.92-1.22ZM20.49 17.27c-.51 1.18-.76 1.71-1.42 2.75-.92 1.41-2.21 3.18-3.81 3.2-1.43.01-1.79-.93-3.73-.92-1.94 0-2.34.94-3.77.92-1.6-.02-2.82-1.61-3.74-3.03-2.56-3.94-2.83-8.56-1.25-11.02 1.12-1.75 2.9-2.77 4.57-2.77 1.69 0 2.76.93 4.16.93 1.36 0 2.18-.93 4.14-.93 1.48 0 3.04.8 4.16 2.19-3.65 2-3.06 7.21.63 8.68Z" />
    </svg>
  );
}

function GooglePlayIcon(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M3.52 2.22c-.32.34-.52.86-.52 1.53v16.5c0 .67.2 1.19.52 1.53l.07.06L12.83 12v-.22L3.59 2.16l-.07.06Z" />
      <path d="m15.91 15.1-3.08-3.1v-.22l3.08-3.1.07.04 3.65 2.08c1.04.59 1.04 1.56 0 2.15l-3.65 2.08-.07.07Z" />
      <path d="m15.98 15.03-3.15-3.15-9.31 9.4c.5.53 1.31.59 2.22.07l10.24-5.82Z" />
      <path d="M15.98 8.72 5.74 2.9c-.91-.52-1.72-.46-2.22.07l9.31 9.4 3.15-3.65Z" />
    </svg>
  );
}

const downloadButtonContent: Record<
  MobileDevice,
  {
    eyebrow: string;
    label: string;
    ariaLabel: string;
    href: string;
    Icon: (props: SVGProps<SVGSVGElement>) => ReactElement;
  }
> = {
  android: {
    eyebrow: "Android app",
    label: "Get it on Google Play",
    ariaLabel: "Get Monra eSIM on Google Play",
    href: MONRA_ESIM_ANDROID_DOWNLOAD_URL,
    Icon: GooglePlayIcon,
  },
  iphone: {
    eyebrow: "iPhone app",
    label: "Download on the App Store",
    ariaLabel: "Download Monra eSIM on the App Store",
    href: MONRA_ESIM_IOS_DOWNLOAD_URL,
    Icon: AppleLogoIcon,
  },
};

interface FlagAvatarProps {
  destination: Destination;
  className: string;
  loading?: "eager" | "lazy";
}

function FlagAvatar({
  destination,
  className,
  loading = "lazy",
}: FlagAvatarProps) {
  const countryCode = destination.code.toLowerCase();

  return (
    <span
      className={`flag-avatar inline-flex shrink-0 overflow-hidden rounded-lg ${className}`}
    >
      <img
        src={`https://flagcdn.com/w160/${countryCode}.png`}
        srcSet={`https://flagcdn.com/w80/${countryCode}.png 80w, https://flagcdn.com/w160/${countryCode}.png 160w`}
        sizes="56px"
        width="160"
        height="107"
        loading={loading}
        alt=""
        className="h-full w-full object-cover"
        aria-hidden="true"
      />
    </span>
  );
}

function DownloadButton({ tone = "default" }: { tone?: "default" | "onDark" }) {
  const [visitorDevice, setVisitorDevice] = useState<VisitorDevice>(() =>
    getVisitorDevice(),
  );

  useEffect(() => {
    setVisitorDevice(getVisitorDevice());
  }, []);

  const isOnDark = tone === "onDark";
  const buttonClass = isOnDark
    ? "border border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
    : "border border-border bg-background text-foreground hover:bg-muted";
  const eyebrowClass = isOnDark
    ? "text-primary-foreground/65"
    : "text-muted-foreground";

  if (visitorDevice === "laptop") {
    return (
      <div
        className="mx-auto grid w-auto gap-2 sm:mx-0 sm:grid-cols-2"
        aria-label="Download Monra eSIM"
      >
        {(["iphone", "android"] as const).map((device) => {
          const {
            eyebrow: storeEyebrow,
            label: storeLabel,
            ariaLabel: storeAriaLabel,
            href: storeHref,
            Icon: StoreIcon,
          } = downloadButtonContent[device];

          return (
            <a
              key={device}
              href={storeHref}
              target="_blank"
              rel="noreferrer"
              className={`download-button inline-flex w-auto min-w-[12.5rem] items-center justify-start gap-3 rounded-xl px-5 py-3 transition-colors ${buttonClass}`}
              aria-label={storeAriaLabel}
            >
              <StoreIcon className="h-7 w-7 shrink-0" aria-hidden="true" />
              <span className="text-left">
                <span className={`block text-[10px] font-medium leading-none ${eyebrowClass}`}>
                  {storeEyebrow}
                </span>
                <span className="mt-1 block text-sm font-semibold leading-none">
                  {storeLabel}
                </span>
              </span>
            </a>
          );
        })}
      </div>
    );
  }

  const { eyebrow, label, ariaLabel, href, Icon } =
    downloadButtonContent[visitorDevice];

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`download-button inline-flex w-auto min-w-[13.5rem] items-center justify-start gap-3 rounded-xl px-5 py-3 transition-colors ${buttonClass}`}
      aria-label={ariaLabel}
    >
      <Icon className="h-7 w-7 shrink-0" aria-hidden="true" />
      <span className="text-left">
        <span className={`block text-[10px] font-medium leading-none ${eyebrowClass}`}>
          {eyebrow}
        </span>
        <span className="mt-1 block text-base font-semibold leading-none">
          {label}
        </span>
      </span>
    </a>
  );
}

interface BundleSummaryProps {
  bundlesByCountry: Record<string, PublicDestinationBundles>;
  status: LandingBundleStatus;
}

function DestinationPreview({ bundlesByCountry, status }: BundleSummaryProps) {
  const [previewIndex, setPreviewIndex] = useState(0);
  const destination = destinations[previewIndex];
  const summary = getDestinationPlanSummary(
    destination,
    bundlesByCountry,
    status,
  );

  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reduceMotion.matches) return;

    const intervalId = window.setInterval(() => {
      setPreviewIndex(
        (currentIndex) => (currentIndex + 1) % destinations.length,
      );
    }, 2800);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <div
      className="destination-preview-panel mx-auto"
      aria-label="Destination availability preview"
    >
      <div className="hero-planner rounded-2xl bg-card p-4 sm:p-6">
        <h2 className="font-display text-2xl tracking-[-0.03em] text-foreground sm:text-3xl">
          Plan before you land.
        </h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          Choose your destination, check starting prices, and get ready before
          departure.
        </p>

        <div className="route-card mt-5 rounded-2xl bg-muted p-3 sm:mt-7 sm:p-4">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-esim-deep">
                Prepare
              </p>
              <p className="mt-1 text-xs font-semibold text-foreground sm:text-sm">
                Before takeoff
              </p>
            </div>
            <div
              className="flex items-center gap-1.5 text-esim-deep"
              aria-hidden="true"
            >
              <span className="h-px w-4 border-t border-dashed border-esim-deep/45 sm:w-7" />
              <Plane className="h-4 w-4" />
              <span className="h-px w-4 border-t border-dashed border-esim-deep/45 sm:w-7" />
            </div>
            <div className="min-w-0 text-right">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-esim-deep">
                Arrive
              </p>
              <div
                key={`${destination.code}-route`}
                className="destination-swap mt-1 flex items-center justify-end gap-2"
              >
                <FlagAvatar
                  destination={destination}
                  loading="eager"
                  className="h-5 w-5"
                />
                <p className="truncate text-xs font-semibold text-foreground sm:text-sm">
                  {destination.name}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              Featured destination
            </p>
            <div className="destination-preview mt-3 flex w-full items-center justify-between gap-2 rounded-2xl bg-muted p-3 sm:p-4">
              <div
                key={destination.code}
                className="destination-swap flex min-w-0 items-center gap-3"
              >
                <FlagAvatar
                  destination={destination}
                  loading="eager"
                  className="h-10 w-10"
                />
                <div className="min-w-0">
                  <p className="truncate font-semibold text-foreground">
                    {destination.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {destination.region}
                  </p>
                </div>
              </div>
              <span className="hidden text-[11px] font-semibold uppercase tracking-[0.14em] text-esim-deep min-[360px]:inline-flex">
                From {summary.fromPrice}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-1.5 text-center text-[11px] font-medium leading-tight text-foreground sm:gap-2 sm:text-xs">
            {["Digital install", "No SIM swap", "Flexible plans"].map(
              (feature) => (
                <p
                  key={feature}
                  className="preview-feature rounded-2xl bg-muted px-1 py-3 sm:px-2"
                >
                  <Check className="mx-auto mb-2 h-4 w-4 text-esim-deep" />
                  {feature}
                </p>
              ),
            )}
          </div>
        </div>

        <Button
          asChild
          size="lg"
          className="mt-5 w-full bg-esim px-3 text-xs text-esim-foreground hover:bg-esim-hover min-[360px]:text-sm sm:px-5"
        >
          <a href="#destinations">
            Browse popular destinations <ArrowRight />
          </a>
        </Button>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Plan details are confirmed before checkout.
        </p>
      </div>
    </div>
  );
}

interface DestinationPlansDialogProps {
  destination: Destination | null;
  summary: DestinationPlanSummary | null;
  onOpenChange: (open: boolean) => void;
}

function DestinationPlansDialog({
  destination,
  summary,
  onOpenChange,
}: DestinationPlansDialogProps) {
  const plans = summary?.plans ?? [];
  const isOpen = destination !== null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[min(42rem,calc(100vh-2rem))] overflow-hidden rounded-2xl border-border bg-card p-0 sm:max-w-2xl">
        {destination && summary && (
          <>
            <DialogHeader className="border-b border-border bg-muted px-5 py-5 text-left sm:px-6">
              <div className="flex items-start gap-4 pr-8">
                <FlagAvatar
                  destination={destination}
                  className="h-12 w-12 sm:h-14 sm:w-14"
                />
                <div className="min-w-0">
                  <DialogTitle className="font-display text-3xl tracking-[-0.03em] text-foreground">
                    {destination.name} eSIM plans
                  </DialogTitle>
                  <DialogDescription className="mt-2 text-sm leading-6">
                    Starting from {summary.fromPrice}.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="max-h-[24rem] overflow-y-auto px-5 py-4 sm:px-6">
              {plans.length > 0 ? (
                <div className="space-y-2.5">
                  {plans.map((plan) => (
                    <div
                      key={`${destination.code}-${plan.data}-${plan.validityDays ?? "open"}-${plan.price}`}
                      className="grid gap-3 rounded-2xl border border-border bg-muted p-4 sm:grid-cols-[1fr_auto] sm:items-center"
                    >
                      <div className="min-w-0">
                        <p className="font-display text-2xl leading-none tracking-[-0.03em] text-foreground">
                          {plan.data}
                        </p>
                        <p className="mt-2 text-sm text-muted-foreground">
                          {plan.validityDays
                            ? `${plan.validityDays} days`
                            : "Validity shown before checkout"}
                          <span className="px-2 text-border">|</span>
                          {plan.speed}
                        </p>
                      </div>
                      <div className="flex items-end justify-between gap-4 sm:block sm:text-right">
                        <p className="text-xs font-semibold uppercase text-muted-foreground">
                          Price
                        </p>
                        <p className="font-display text-2xl leading-none tracking-[-0.03em] text-esim-deep">
                          {formatPlanPrice(plan.price)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-border bg-muted p-5">
                  <p className="font-semibold text-foreground">
                    {summary.status === "loading"
                      ? "Checking current plans"
                      : "Current plan details are available after download."}
                  </p>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    The destination is listed on the landing page, but live plan
                    rows are not available in this session.
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-border/70 bg-background/60 px-5 py-4 text-center sm:px-6">
              <p className="text-sm font-semibold text-esim-deep">
                Plans are available in the Monra eSIM app.
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PopularDestinations({ bundlesByCountry, status }: BundleSummaryProps) {
  const [selectedDestinationCode, setSelectedDestinationCode] = useState<
    string | null
  >(null);
  const selectedDestination =
    destinations.find(
      (destination) => destination.code === selectedDestinationCode,
    ) ?? null;
  const selectedSummary = selectedDestination
    ? getDestinationPlanSummary(selectedDestination, bundlesByCountry, status)
    : null;

  return (
    <section
      id="destinations"
      className="destinations-stage px-4 py-14 sm:px-6 sm:py-28 lg:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-5 sm:gap-6 lg:grid-cols-[minmax(0,0.52fr)_minmax(0,0.48fr)] lg:items-end lg:gap-12">
          <div>
            <p className="esim-eyebrow monra-eyebrow">Popular destinations</p>
            <h2 className="display-title mt-5 tracking-[-0.03em] text-foreground sm:mt-6">
              Start with where travellers are landing.
            </h2>
          </div>
          <div className="max-w-md lg:justify-self-end lg:pb-1">
            <p className="text-[15px] leading-7 text-muted-foreground sm:text-base sm:leading-7">
              Pick a popular destination to preview the trip, then open Monra eSIM
              when you are ready to choose a plan.
            </p>
            <p className="mt-3 text-[13px] font-medium text-esim-deep">
              {destinations.length} countries
            </p>
          </div>
        </div>

        <div className="destination-grid-card mt-10 min-w-0 sm:mt-14">
          <div className="destination-options-scroll flex w-full max-w-full snap-x snap-mandatory gap-4 overflow-x-auto pb-1 sm:grid sm:grid-cols-2 sm:overflow-visible sm:pb-0 sm:gap-5 lg:grid-cols-4">
            {destinations.map((option, index) => {
              const summary = getDestinationPlanSummary(
                option,
                bundlesByCountry,
                status,
              );
              const planPreview = summary.plans.slice(0, 2);

              return (
                <button
                  key={option.code}
                  type="button"
                  onClick={() => setSelectedDestinationCode(option.code)}
                  style={{ animationDelay: `${index * 24}ms` }}
                  className="destination-option destination-option-card group flex shrink-0 snap-start flex-col gap-4 rounded-2xl bg-card p-4 text-left text-foreground sm:w-auto sm:p-5"
                >
                  <div className="flex items-center gap-3">
                    <FlagAvatar
                      destination={option}
                      className="h-11 w-11 sm:h-12 sm:w-12"
                    />
                    <div className="min-w-0">
                      <span className="block truncate text-[15px] font-semibold tracking-[-0.02em] text-foreground sm:text-base">
                        {option.name}
                      </span>
                      <span className="mt-0.5 block truncate text-[13px] leading-5 text-muted-foreground">
                        {option.region}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <span className="block text-[13px] leading-5 text-muted-foreground">
                        From
                      </span>
                      <span className="mt-1 block font-display text-[1.75rem] leading-none tracking-[-0.04em] text-foreground">
                        {summary.fromPrice}
                      </span>
                    </div>
                    <span className="pb-0.5 text-[13px] font-medium text-esim-deep">
                      {summary.planCountLabel}
                    </span>
                  </div>

                  <div className="flex flex-col gap-2">
                    {planPreview.map((plan) => (
                      <div
                        key={`${option.code}-${plan.data}-${plan.validityDays ?? "open"}-${plan.price}`}
                        className="flex items-center justify-between gap-3 rounded-xl bg-muted px-3.5 py-3"
                      >
                        <span className="truncate text-[13px] font-medium leading-5 text-foreground">
                          {formatPlanLabel(plan)}
                        </span>
                        <span className="shrink-0 text-[13px] font-semibold leading-5 text-esim-deep">
                          {formatPlanPrice(plan.price)}
                        </span>
                      </div>
                    ))}
                    {planPreview.length === 0 && (
                      <p className="rounded-xl bg-muted px-3.5 py-3 text-[13px] leading-5 text-muted-foreground">
                        {summary.status === "loading"
                          ? "Checking current plans"
                          : "Open to view current plan options"}
                      </p>
                    )}
                  </div>

                  <div className="mt-auto flex items-center justify-between gap-3 pt-1 text-[13px] font-semibold leading-5 text-esim-deep">
                    <span>
                      {summary.plans.length > planPreview.length
                        ? `+${summary.plans.length - planPreview.length} more`
                        : summary.status === "ready"
                          ? "Live pricing"
                          : "Current pricing"}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      See plans
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          <p className="mt-6 text-[13px] leading-5 text-muted-foreground sm:mt-8">
            More countries are available after download.
          </p>
        </div>
      </div>
      <DestinationPlansDialog
        destination={selectedDestination}
        summary={selectedSummary}
        onOpenChange={(open) => {
          if (!open) setSelectedDestinationCode(null);
        }}
      />
    </section>
  );
}

function TravelUseCases() {
  const [primaryCase, ...secondaryCases] = travelUseCases;
  const PrimaryIcon = primaryCase.icon;

  return (
    <section className="use-cases-stage px-4 py-14 sm:px-6 sm:py-28 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="max-w-3xl">
          <h2 className="display-title text-foreground">
            Travel data for the moments that cannot wait.
          </h2>
          <p className="mt-6 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
            Use Monra eSIM for the everyday travel tasks that become stressful
            without a connection.
          </p>
        </div>

        <div className="mt-9 grid gap-4 sm:mt-12 sm:gap-5 lg:grid-cols-[1.05fr_1.4fr]">
          <article className="use-case-feature relative overflow-hidden rounded-2xl bg-esim-deep p-6 text-esim-foreground sm:p-8 lg:min-h-[26rem]">
            <div className="relative flex h-full flex-col justify-between gap-12">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-esim/35 text-esim-soft">
                <PrimaryIcon className="h-6 w-6" aria-hidden="true" />
              </span>
              <div>
                <h3 className="font-display text-3xl leading-tight tracking-[-0.03em] sm:text-4xl">
                  {primaryCase.title}
                </h3>
                <p className="mt-4 max-w-md text-sm leading-7 text-esim-foreground/72 sm:text-base">
                  {primaryCase.body}
                </p>
              </div>
            </div>
          </article>

          <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
            {secondaryCases.map(({ title, body, icon: Icon }) => (
              <article
                key={title}
                className="use-case-card rounded-2xl bg-muted p-5 sm:p-6"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-esim-soft text-esim-deep">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-6 font-display text-2xl tracking-[-0.03em] text-foreground">
                  {title}
                </h3>
                <p className="mt-3 text-sm leading-7 text-muted-foreground">
                  {body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function StorySection() {
  return (
    <section
      id="why-monra"
      className="story-stage px-4 py-10 sm:px-6 sm:py-16 lg:px-8"
    >
      <div className="mx-auto max-w-4xl">
        <blockquote className="story-quote border-l-2 border-foreground py-1 pl-5 sm:pl-7">
          <p className="font-display text-[clamp(1.35rem,2.8vw,2.05rem)] italic leading-[1.4] text-foreground">
            “We love travelling, but we were tired of expensive roaming,
            last-minute SIM hunts, and eSIM offers that were harder to compare
            than they should be. So we built Monra eSIM for travelers who want
            to plan ahead and get online when the trip begins.”
          </p>
          <footer className="mt-5 flex items-center gap-4 text-sm font-semibold text-esim-deep sm:mt-6">
            <span className="h-px w-12 bg-esim-deep/35" aria-hidden="true" />
            <span>MonraLabs</span>
          </footer>
        </blockquote>
      </div>
    </section>
  );
}

function ComparisonSection() {
  return (
    <section className="comparison-stage px-4 py-14 sm:px-6 sm:py-28 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-7 lg:grid-cols-[0.45fr_0.55fr] lg:items-end">
          <div>
            <h2 className="display-title text-foreground">
              Pick the travel data route that fits the trip.
            </h2>
          </div>
          <p className="max-w-xl text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8 lg:justify-self-end">
            Compare the common ways travellers get online before choosing what
            works for your destination, timing, and device.
          </p>
        </div>

        <div className="comparison-grid mt-9 grid gap-4 sm:mt-12 lg:grid-cols-[0.8fr_0.8fr_1.1fr]">
          {comparisonOptions.map((option) => (
            <article
              key={option.name}
              className={`comparison-card rounded-2xl p-5 sm:p-7 ${
                option.featured
                  ? "comparison-card-featured bg-esim-deep text-esim-foreground"
                  : "bg-muted text-foreground"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display text-3xl tracking-[-0.03em]">
                    {option.name}
                  </h3>
                  <p
                    className={`mt-3 text-sm leading-7 ${
                      option.featured
                        ? "text-esim-foreground/72"
                        : "text-muted-foreground"
                    }`}
                  >
                    {option.summary}
                  </p>
                </div>
                {option.featured && (
                  <span className="shrink-0 rounded-lg bg-esim/35 px-2.5 py-1 text-[11px] font-semibold tracking-[0.08em] text-esim-soft">
                    Best for planning
                  </span>
                )}
              </div>

              <ul className="mt-6 space-y-3">
                {option.details.map((detail) => {
                  const DetailIcon = detail.tone === "con" ? X : Check;

                  return (
                    <li
                      key={detail.text}
                      className={`flex gap-3 text-sm font-medium leading-6 ${
                        option.featured
                          ? "text-esim-foreground/82"
                          : "text-foreground/76"
                      }`}
                    >
                      <DetailIcon
                        className={`mt-0.5 h-4 w-4 shrink-0 ${
                          detail.tone === "con"
                            ? "text-muted-foreground"
                            : "text-esim-soft"
                        }`}
                        aria-hidden="true"
                      />
                      <span>{detail.text}</span>
                    </li>
                  );
                })}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  return (
    <section
      id="how-it-works"
      className="setup-stage monra-band px-4 py-14 sm:px-6 sm:py-28 lg:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-7 lg:grid-cols-[0.7fr_0.3fr] lg:items-end">
          <div className="max-w-3xl">
            <p className="esim-eyebrow monra-eyebrow">Monra eSIM</p>
            <h2 className="display-title mt-4 text-foreground">
              Three steps. One less thing to solve abroad.
            </h2>
          </div>
          <p className="text-base leading-7 text-muted-foreground lg:pb-1">
            Purchase, install, and activate from the phone you already carry.
          </p>
        </div>
        <div className="setup-route mt-9 grid gap-3 sm:mt-12 sm:gap-4 lg:grid-cols-3">
          {journeySteps.map((step, index) => {
            const featured = index === journeySteps.length - 1;

            return (
              <article
                key={step.number}
                className={`setup-card relative rounded-2xl p-5 sm:p-8 ${
                  featured
                    ? "setup-card-featured bg-esim-deep text-esim-foreground"
                    : "bg-muted text-foreground"
                }`}
              >
                <p
                  className={`flex h-11 w-11 items-center justify-center rounded-xl font-sans text-sm font-semibold tabular-nums ${
                    featured
                      ? "bg-esim/35 text-esim-soft"
                      : "bg-esim-soft text-esim-deep"
                  }`}
                >
                  {step.number}
                </p>
                <h3 className="mt-6 font-display text-2xl tracking-[-0.03em] sm:mt-10">
                  {step.title}
                </h3>
                <p
                  className={`mt-4 text-sm leading-7 ${
                    featured
                      ? "text-esim-foreground/72"
                      : "text-muted-foreground"
                  }`}
                >
                  {step.description}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CompatibilitySection() {
  return (
    <section
      id="compatibility"
      className="compatibility-stage px-4 py-14 sm:px-6 sm:py-28 lg:px-8"
    >
      <div className="mx-auto grid max-w-7xl gap-9 sm:gap-12 lg:grid-cols-[0.43fr_0.57fr] lg:items-start">
        <div>
          <p className="esim-eyebrow monra-eyebrow">Before you travel</p>
          <h2 className="display-title mt-5 text-foreground">
            Set up with confidence.
          </h2>
          <p className="mt-6 max-w-md text-base leading-7 text-muted-foreground">
            Monra eSIM is designed for straightforward travel data. Check these
            essentials before choosing a plan.
          </p>
          <Button
            asChild
            size="lg"
            className="mt-8 w-full bg-esim px-4 text-esim-foreground hover:bg-esim-hover sm:mt-9 sm:w-auto sm:px-5"
          >
            <a href={MONRA_ESIM_DOWNLOAD_URL} target="_blank" rel="noreferrer">
              Check available plans <ArrowRight />
            </a>
          </Button>
        </div>
        <div className="readiness-panel rounded-2xl bg-card p-4 sm:p-5">
          {readinessNotes.map((item) => (
            <article
              key={item.title}
              className="readiness-row flex gap-4 py-5 first:pt-2 last:pb-2 sm:gap-5 sm:py-6"
            >
              <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-esim-soft text-esim-deep">
                <Check className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase text-esim-deep/75">
                  {item.label}
                </p>
                <h3 className="mt-2 font-display text-2xl tracking-[-0.03em] text-foreground">
                  {item.title}
                </h3>
                <p className="mt-2 max-w-2xl text-sm leading-7 text-muted-foreground sm:text-base">
                  {item.description}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function StepByStepGuide() {
  return (
    <ol className="support-step-list bg-card">
      {troubleshootingSteps.map((step, index) => {
        const stepNumber = String(index + 1).padStart(2, "0");

        return (
          <li
            key={step.title}
            className="support-step grid gap-4 px-5 py-5 sm:grid-cols-[auto_1fr] sm:gap-5 sm:px-7 sm:py-6"
          >
            <div>
              <span className="support-step-number flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted text-sm font-semibold text-foreground">
                {stepNumber}
              </span>
            </div>
            <div className="min-w-0">
              <h4 className="font-display text-2xl tracking-[-0.03em] text-foreground">
                {step.title}
              </h4>
              <p className="mt-2 text-sm leading-7 text-muted-foreground sm:text-base">
                {step.description}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function TroubleshootingSection() {
  const [activeSupportTab, setActiveSupportTab] =
    useState<SupportTabId>("faqs");
  const activeSupportItem =
    supportQuickLinks.find((item) => item.id === activeSupportTab) ??
    supportQuickLinks[0];
  const ActiveSupportIcon = activeSupportItem.icon;
  const activeContent = {
    troubleshooting: {
      title: "Step-by-step guide",
      description:
        "Follow these checks in order. Keep the eSIM installed while troubleshooting.",
      items: troubleshootingSteps.map((step, index) => ({
        question: `${String(index + 1).padStart(2, "0")} ${step.title}`,
        answer: step.description,
      })),
    },
    issues: {
      title: "Common issues",
      description:
        "Quick fixes for connection, QR code, speed, and data problems.",
      items: troubleshootingIssues.map((issue) => ({
        question: issue.title,
        answer: issue.description,
      })),
    },
    faqs: {
      title: "FAQs",
      description:
        "The practical details to check before travelling with Monra.",
      items: esimFaqs,
    },
  }[activeSupportTab];

  return (
    <section id="support" className="support-help-stage">
      <div className="support-help-hero relative px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <p className="esim-eyebrow monra-eyebrow">Support</p>
          <h2 className="display-title mt-4 text-foreground">
            Help & Support
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
            Resolve setup issues, check common fixes, and review the details
            travelers ask about most.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 pt-8 sm:px-6 lg:px-8">
        <div
          className="support-tabs grid grid-cols-3 gap-1 rounded-2xl p-1"
          role="tablist"
          aria-label="Support topics"
        >
          {supportQuickLinks.map(({ id, title }) => {
            const isActive = activeSupportTab === id;

            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls="support-panel"
                className={`support-tab-button rounded-xl px-2 py-2.5 text-center transition-colors duration-200 sm:px-4 sm:py-3 ${isActive ? "is-active" : ""}`}
                onClick={() => setActiveSupportTab(id)}
              >
                <span className="block truncate text-xs font-semibold text-foreground sm:text-sm">
                  {title}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 sm:py-16 lg:px-8">
        <div
          id="support-panel"
          role="tabpanel"
          aria-label={activeSupportItem.title}
          className="support-panel monra-card overflow-hidden border border-border/65"
        >
          <div className="border-b border-border/65 bg-card px-5 py-6 sm:px-7 sm:py-8">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-esim-soft text-esim-deep">
                <ActiveSupportIcon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h3 className="font-display text-3xl tracking-[-0.03em] text-foreground">
                  {activeContent.title}
                </h3>
                <p className="mt-3 text-base leading-7 text-muted-foreground">
                  {activeContent.description}
                </p>
              </div>
            </div>
          </div>

          {activeSupportTab === "troubleshooting" ? (
            <StepByStepGuide />
          ) : (
            activeContent.items.map((item) => (
              <FAQItem
                key={item.question}
                question={item.question}
                answer={item.answer}
                accentColor="text-esim-deep"
              />
            ))
          )}
        </div>

        <p className="mx-auto mt-6 max-w-2xl text-center text-sm leading-6 text-muted-foreground">
          Still have questions or something not working as expected?{" "}
          <a
            href="mailto:support@monralabs.com"
            className="font-semibold text-esim-deep transition-opacity hover:opacity-75"
          >
            Contact us
          </a>{" "}
          and we will help you sort it out.
        </p>
      </div>
    </section>
  );
}

export default function EsimLandingPage() {
  const { bundlesByCountry, status } = useLandingBundles();

  return (
    <div className="min-h-screen bg-background animate-fade-in">
      <Navbar links={mainNavLinks} logoVariant="esim" isTransparent />

      <main>
        <section id="esim" className="hero-stage overflow-hidden">
          <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 pb-10 pt-24 sm:gap-12 sm:px-6 sm:pb-20 sm:pt-28 lg:min-h-svh lg:grid-cols-[1.03fr_0.78fr] lg:gap-16 lg:px-8 lg:pb-16 lg:pt-28">
            <div className="monra-reveal lg:pb-8">
              <h1 className="display-title max-w-3xl text-foreground">
                Touch down.
                <em className="block">Already online.</em>
              </h1>
              <p className="lede mt-6 sm:mt-8 sm:text-xl sm:leading-8">
                Get mobile data abroad with Monra eSIM. Choose your destination,
                set up from your phone, and skip the kiosk hunt when the trip
                begins.
              </p>
              <div className="mt-8 flex w-full justify-center sm:mt-10 sm:w-auto sm:justify-start">
                <DownloadButton />
              </div>
            </div>

            <div className="relative w-full monra-reveal lg:w-[28.5rem] lg:justify-self-end [animation-delay:140ms]">
              <div className="relative">
                <DestinationPreview
                  bundlesByCountry={bundlesByCountry}
                  status={status}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="proof-strip px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
          <div className="proof-grid mx-auto grid max-w-7xl gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
            {proofPoints.map(({ title, detail, icon: Icon }, index) => (
              <div
                key={title}
                className="proof-item flex items-start gap-4 p-4 sm:p-5"
              >
                <span className="proof-icon mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-esim-soft text-esim-deep">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-sans text-[0.68rem] font-semibold tabular-nums text-esim-deep/70">
                      0{index + 1}
                    </span>
                    <p className="text-sm font-semibold text-foreground sm:text-base">
                      {title}
                    </p>
                  </div>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    {detail}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <PopularDestinations
          bundlesByCountry={bundlesByCountry}
          status={status}
        />

        <HowItWorksSection />

        <TravelUseCases />

        <StorySection />

        <ComparisonSection />

        <CompatibilitySection />

        <TroubleshootingSection />

        <section
          id="download"
          className="download-stage px-4 py-14 sm:px-6 sm:py-20 lg:px-8"
        >
          <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 sm:gap-10 lg:flex-row lg:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary-foreground/55">
                Available to download
              </p>
              <h2 className="display-title mt-5 max-w-3xl text-primary-foreground">
                Download once. <em className="text-primary-foreground/72">Travel lighter.</em>
              </h2>
              <p className="mt-6 max-w-xl text-base leading-7 text-primary-foreground/68">
                Open current destinations, compare data options, and keep trip
                connectivity ready from the phone you already carry.
              </p>
            </div>
            <div className="flex w-full flex-col items-center gap-4 sm:w-auto sm:items-start lg:items-end">
              <DownloadButton tone="onDark" />
              <p className="text-center text-xs font-medium text-primary-foreground/55 sm:text-left lg:text-right">
                Plan availability varies by destination and device.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
